/**
 * Thin fetch wrapper for the Wyso backend.
 *
 * Components never fetch — pages do, via TanStack Query (see src/lib/queryClient.js). This
 * module is the single place that knows the base URL, builds request URLs, and normalises
 * failures into a typed {@link ApiError} so callers can tell a server error (status) from a
 * network/offline failure (no status). Keep it dependency-free and small.
 *
 * Base URL: `import.meta.env.VITE_API_BASE_URL`, falling back to http://localhost:3000 for
 * local dev. Prod injects the real origin via env. See `.env.example`.
 *
 * Import-cycle note: auth.js statically imports apiPost from here. To avoid a static cycle,
 * this module imports refreshAccessToken DYNAMICALLY (inside the 401 handler / no-token path).
 */

import { getAccessToken, getRefreshToken } from './authStore.js';
import { markAttributionSent, pendingAttribution } from './attribution.js';

/** The resolved API origin. All paths are joined onto this. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * A normalised request failure.
 * `status` is the HTTP status for a server error, or `0` for a network/offline failure.
 */
export class ApiError extends Error {
  constructor(message, status = 0, detail = undefined, fields = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    /** true when the request never reached the server (network down / CORS / offline). */
    this.offline = status === 0;
    /** Normalised backend error detail (FastAPI `detail`), when the body carried one. */
    this.detail = detail;
    /** The wire fields a 422 names (`loc[1]` of each `detail` entry), e.g. `['username']`. */
    this.fields = fields;
  }
}

/**
 * Read a failed response's body and normalise FastAPI's `detail` field.
 * `detail` becomes a string: a string `detail` (HTTPException) is used as-is; an array of
 * `{loc, msg}` (422 validation errors) has its messages joined; anything else (empty/non-JSON
 * body, no `detail`) yields undefined. `fields` lists each array entry's `loc[1]` — the wire
 * field it names — and is `[]` otherwise. Never throws — a 4xx may have an empty or non-JSON
 * body.
 *
 * @param {Response} res
 * @returns {Promise<{ detail: string | undefined, fields: string[] }>}
 */
async function readErrorDetail(res) {
  try {
    const body = await res.json();
    const detail = body?.detail;
    if (typeof detail === 'string') return { detail, fields: [] };
    if (Array.isArray(detail)) {
      return {
        detail: detail.map((d) => d?.msg).filter(Boolean).join(' ') || undefined,
        fields: detail.map((d) => d?.loc?.[1]).filter((f) => typeof f === 'string'),
      };
    }
    return { detail: undefined, fields: [] };
  } catch {
    return { detail: undefined, fields: [] };
  }
}

/** Build an ApiError from a failed response, carrying its normalised detail and fields. */
async function errorFrom(res, message) {
  const { detail, fields } = await readErrorDetail(res);
  return new ApiError(message, res.status, detail, fields);
}

/**
 * Guard: in production, the API base URL must be set and must not point to localhost.
 * Pure function — takes explicit args so tests can call it without touching real env.
 *
 * @param {{ mode: string, baseUrl: string }} params
 */
export function assertProdApiBase({ mode, baseUrl }) {
  if (mode !== 'production') return;
  if (!baseUrl || /localhost|127\.0\.0\.1/.test(baseUrl)) {
    throw new Error(
      `VITE_API_BASE_URL must be set to a real origin in production (got: ${JSON.stringify(baseUrl)})`,
    );
  }
}

assertProdApiBase({ mode: import.meta.env.MODE, baseUrl: API_BASE_URL });

// ---------------------------------------------------------------------------
// Internal shared request helper
// ---------------------------------------------------------------------------

/**
 * @param {'GET'|'POST'|'DELETE'} method
 * @param {string} path
 * @param {{ body?: unknown, auth?: boolean, signal?: AbortSignal, keepalive?: boolean }} opts
 * @returns {Promise<unknown>}
 */
async function request(method, path, { body, auth = true, signal, keepalive } = {}) {
  const url = `${API_BASE_URL}${path}`;

  /**
   * Build headers and run one fetch. Returns the Response.
   * @param {string | undefined} accessToken
   * @returns {Promise<Response>}
   */
  async function doFetch(accessToken) {
    /** @type {Record<string, string>} */
    const headers = { Accept: 'application/json' };
    const multipart = body instanceof FormData;
    if (method === 'POST' && !multipart) headers['Content-Type'] = 'application/json';
    if (auth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const init = { method, headers, signal };
    // keepalive lets a request started in `pagehide` / a hidden tab outlive the page.
    if (keepalive) init.keepalive = true;
    if (body !== undefined) init.body = multipart ? body : JSON.stringify(body);

    try {
      return await fetch(url, init);
    } catch (error) {
      if (signal?.aborted || error?.name === 'AbortError') throw error;
      throw new ApiError(`Network request to ${path} failed`, 0);
    }
  }

  // --- resolve the initial access token ---
  let token = auth ? getAccessToken() : undefined;

  if (auth && !token && getRefreshToken()) {
    // Post-reload path: refresh token present in storage but no access token in memory
    // (e.g. after a hard reload) — mint one before the first authed request.
    const { refreshAccessToken } = await import('./auth.js');
    token = await refreshAccessToken();
  }

  let res = await doFetch(token);

  // --- 401 refresh-and-retry (authed calls not under /auth/*) ---
  const isAuthPath = path.startsWith('/auth/');
  if (res.status === 401 && auth && !isAuthPath) {
    const { refreshAccessToken } = await import('./auth.js');
    const newToken = await refreshAccessToken(); // throws AuthError on terminal failure
    res = await doFetch(newToken);
    if (res.status === 401) {
      throw await errorFrom(res, `Request to ${path} failed (401)`);
    }
  }

  if (!res.ok) {
    const error = await errorFrom(res, `Request to ${path} failed (${res.status})`);
    const retryAfter = res.headers?.get('Retry-After');
    if (retryAfter) error.retryAfterMs = Math.max(0, Number.isFinite(Number(retryAfter))
      ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now());
    throw error;
  }
  return res.status === 204 ? undefined : res.json();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * GET `path` (joined onto API_BASE_URL) and parse JSON.
 * Throws {@link ApiError} on a non-2xx response or a network failure.
 *
 * @param {string} path
 * @param {{ auth?: boolean }} [options]
 */
export function apiGet(path, options) {
  return request('GET', path, { auth: options?.auth ?? true, signal: options?.signal });
}

/**
 * POST `path` with `body` (joined onto API_BASE_URL) and parse JSON.
 * Throws {@link ApiError} on a non-2xx response or a network failure.
 *
 * @param {string} path
 * @param {unknown} body
 * @param {{ auth?: boolean, signal?: AbortSignal, keepalive?: boolean }} [options]
 */
export function apiPost(path, body, options) {
  return request('POST', path, { body, auth: options?.auth ?? true, signal: options?.signal,
    keepalive: options?.keepalive });
}

// ---------------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------------

/** GET /api/preferences → { about, aboutOptions, interests, dreams }. */
export const getPreferences = () => apiGet('/api/preferences');

/** POST /api/preferences (partial patch) → updated persisted values. */
export const savePreferences = (patch) => apiPost('/api/preferences', patch);

/** GET /api/home/learner → { learner, showOnboarding }. `vibe` was removed with the Home v2 redesign. */
export const getHomeLearner = () => apiGet('/api/home/learner');

/** GET /api/home/quests → { weekStart, resetsLabel?, quests: Quest[] } — the current IST week. See src/pages/home/Quests/QUESTS_DATA.md. */
export const getHomeQuests = () => apiGet('/api/home/quests');

/** GET /api/home/leaderboard → the card summary (you + a 5-row window around rank 1, 2 and you). See src/pages/home/Leaderboard/LEADERBOARD_DATA.md. */
export const getHomeLeaderboard = () => apiGet('/api/home/leaderboard');

/** GET /api/home/leaderboard/full → every ranked row (missions + losCompleted + convinces); the UI windows it. Fetched lazily on open. */
export const getHomeLeaderboardFull = () => apiGet('/api/home/leaderboard/full');

/**
 * GET /api/home/invites → { code, invitesTotal, invitesUsed, auraPerInvite }.
 *
 * The student's own referral code and how many of their invitations are left. Fetched LAZILY,
 * only when the invite dialog opens — most students never open it, and Home's first paint must
 * not carry it.
 *
 * `remaining` is DERIVED client-side (`invitesTotal - invitesUsed`), never sent: a third field
 * that has to agree with the other two is a drift bug waiting to happen. See
 * `src/pages/home/HOME_DATA.md`.
 */
export const getHomeInvites = () => apiGet('/api/home/invites');

/**
 * POST /api/onboarding/complete → durably marks the first-run onboarding tour done, so it
 * never shows again. Empty body; the student is identified by the auth token. Response is
 * `{ is_onboarded: true, new_awards: NewAward[] }` (snake_case `new_awards`; only ever carries
 * `onboarding-complete`, the one award this route can grant). `new_awards` IS read — callers
 * normalise it with `normalizeNewAwards` to drive the onboarding award modal. Callers hide
 * the tour optimistically and invalidate the learner query so `showOnboarding` refetches false.
 */
export const markOnboardingComplete = () => apiPost('/api/onboarding/complete', {});

/**
 * GET /api/awards (authed) → `{ awards: AwardView[] }` — the whole catalogue, in catalogue
 * order. Normalise with `normalizeAwardsView` (`@shared/awards/awardAdapters.js`); never read
 * the wire in a component. See docs/wiki/api-integration.md.
 */
export const getAwards = () => apiGet('/api/awards');

/**
 * GET /api/home/resume → { subjects, defaultSubject? } — the whole "Learn new things" section
 * in one call: always all three subjects (maths, science, ai, in that fixed order, an empty one
 * sending `concepts: []` rather than being omitted), each concept carrying its full
 * `objectives[]` with per-row `completed`, and `recommended` set by the server on at most one
 * concept per subject.
 *
 * Reshaped in Home v3 (`docs/superpowers/specs/2026-09-21-home-v3-api-contract.md`); it used to
 * return `{ inProgress, recommended }`. Consumers MUST degrade a payload still carrying the old
 * shape — or any 200 whose `subjects` is missing or not an array — to the section's empty
 * state, never crash: that is exactly what production serves until the backend ships v3.
 */
export const getHomeResume = () => apiGet('/api/home/resume');

/**
 * GET /api/home/roster → { subjects }. Authed — sends the Bearer token like learner/resume.
 * Unchanged on the wire by Home v3; only its consumer moved — the `/roster` page calls it, Home
 * no longer does. This is the FULL library across all grades, not the student's next steps.
 */
export const getHomeRoster = () => apiGet('/api/home/roster');

/**
 * GET /api/home/missions → { missions: RecallMission[] } — weak/decaying concepts for the
 * home Recall Missions deck, most urgent first.
 * Each mission carries `subject` and `setId` (the pre-generated question set to play). A
 * mission missing `setId` is not navigable (HomeRoute warns and refuses). Each also carries
 * `hasAccess` / `accessMessage`: a later set is locked until enough of the concept's LOs are
 * done, shown on the card only (`/api/recall/start` does not enforce it).
 * See src/pages/home/HOME_DATA.md for the field contract.
 */
export const getHomeMissions = () => apiGet('/api/home/missions');

/**
 * GET /api/home/convince → { grade, default_subject, max_aura, stats, subjects[] } — Home's
 * "Can you convince a buddy?" section. snake_case on purpose: each chapter is field-for-field a
 * GET /api/notes chapter plus `next {slug, title, page_number}` and `all_convinced`. A 404 means
 * the endpoint isn't deployed yet and the section hides itself. Contract:
 * docs/superpowers/specs/2026-10-26-home-convince-api-contract.md.
 */
export const getHomeConvince = () => apiGet('/api/home/convince');

/** GET /api/notes → { grade, subjects[] } — the student's grade only. Contract: docs/superpowers/specs/2026-10-18-notes-api-contract.md §1. */
export const getNotesLibrary = () => apiGet('/api/notes');

/** GET /api/notes/{chapter_id} → { chapter_id, subject_key, notes, pages, symbols } (snake_case, passed through). §2. */
export const getNotesChapter = (chapterId) => apiGet(`/api/notes/${encodeURIComponent(String(chapterId))}`);

/**
 * POST /api/notes/visits — one fire-and-forget engagement report per left view (§3).
 * keepalive so it survives a closing tab; never retried; every failure is swallowed because
 * the student can do nothing about it.
 */
export const postNotesVisit = (visit) =>
  apiPost('/api/notes/visits', visit, { keepalive: true }).then(() => undefined, () => undefined);

/**
 * POST /tutoring/start → { session_id, room_name, token, ws_url, mic_onboarded }.
 * Both `conceptId` and `loId` are required by the backend (422 without either) — always sent,
 * even if falsy, so a stale/missing id exercises the same error surface as any other 422.
 * `studentId` is discarded; identity comes from the Bearer token.
 */
export const startTutoring = ({ studentId: _studentId, conceptId, loId }) => {
  if (!conceptId || !loId) {
    console.warn('startTutoring: missing conceptId or loId', { conceptId, loId });
  }
  return apiPost('/tutoring/start', { concept_id: conceptId, lo_id: loId });
};

/**
 * POST /tutoring/micused { session_id } → { ok: true }. Fired at most ONCE per session, the first
 * time the student opens the mic while /tutoring/start said `mic_onboarded: false`: the mic tip
 * has done its job, so the backend stops asking for that student. One-way and idempotent;
 * `session_id` is logged only (the flag lives on the student). Fire-and-forget — see useMicTip.
 * Mirrors /api/recall/hintused.
 */
export const reportMicUsed = (sessionId) => apiPost('/tutoring/micused', { session_id: sessionId });

/**
 * POST /tutoring/end → { session_id, status, aura_points, completed?, summary?, eval_correct?,
 * eval_total?, words_spoken?, new_awards? } (snake_case; normalise with `normalizeTutoringEnd` in
 * `pages/learning-session/end/endAdapters.js`). The optional fields (aside from `new_awards`) are
 * the single-objective session contract
 * (docs/superpowers/specs/2026-09-30-single-lo-session-api-contract.md); until the backend ships
 * them the old three-field body still arrives, and the results screen hides what is missing.
 * Every metric is the backend's — the client computes none.
 *
 * `new_awards: NewAward[]` (default `[]`) is normalised by `normalizeTutoringEnd` into
 * `newAwards`; when any arrive the caller calls `prefetchAwardGrant` (see `useSessionEnd.js`).
 *
 * `auraPointsGained` is the client's cosmetic in-session count, still sent (clamped to a
 * non-negative integer) but ignored by the server: the Aura the student actually earned is the
 * response's `aura_points`. The server makes this call idempotent, so a retry after a failure
 * re-sends the identical body and gets the identical result.
 *
 * `timeTakenSeconds` is the session clock the student saw (whole seconds, clamped to the server's
 * 0..86_400 — out of range is a 422 for the whole call). The server keeps it only for a finished
 * session. Omitted when not a number — old behaviour.
 */
const MAX_TIME_TAKEN_SECONDS = 86_400;
export const endTutoring = ({ sessionId, auraPointsGained = 0, timeTakenSeconds }) =>
  apiPost('/tutoring/end', {
    session_id: sessionId,
    aura_points_gained: Math.max(0, Math.floor(Number(auraPointsGained) || 0)),
    ...(Number.isFinite(timeTakenSeconds) && {
      time_taken_seconds: Math.min(MAX_TIME_TAKEN_SECONDS, Math.max(0, Math.floor(timeTakenSeconds))),
    }),
  });

/**
 * GET /tutoring/next?session_id=… → { next_objective: { concept_id, concept_name, subject,
 * lo_id, lo_name } | null }. What to learn after this session — the backend decides (next LO in
 * the concept, or another concept). Only called once `/tutoring/end` has resolved, so the
 * session's progress is committed. Normalise with `normalizeNextObjective`.
 */
export const getNextObjective = (sessionId) =>
  apiGet(`/tutoring/next?session_id=${encodeURIComponent(sessionId)}`);

/**
 * POST /convince/start { chapter_id, slug } → { session_id, room_name, token, ws_url, mic_onboarded }.
 * Starts a fresh Convince Buddy session on ONE notes concept page; every call is a new session
 * (retakes are always allowed). The agent `wyso-buddy` is dispatched server-side.
 * Contract: docs/superpowers/specs/2026-10-21-convince-buddy-api-contract.md §2.
 */
export const startConvince = ({ chapterId, slug }) =>
  apiPost('/convince/start', { chapter_id: chapterId, slug });

/**
 * POST /convince/end { session_id, reason } → { session_id, completed, total, convinced, verdicts,
 * aura_points, new_awards }. Idempotent: a repeat call returns the same body. Normalise with
 * `normalizeConvinceEnd` (pages/convince/convinceEnd.js). `reason` is 'completed' | 'early_exit'.
 * Contract §6.
 */
export const endConvince = ({ sessionId, reason }) =>
  apiPost('/convince/end', { session_id: sessionId, reason });

/**
 * POST /buddy-call/start { chapter_id, slug, buddy } → { call_id, room_name, token, ws_url }.
 * One phone-like call with a study buddy ('veda' | 'neel') from a notes concept page; `slug` is
 * the card open at the start (later cards go over the `set_card` RPC). 400 = unknown chapter or
 * slug. Contract: docs/superpowers/specs/2026-10-28-buddy-call-api-contract.md §1.
 */
export const startBuddyCall = ({ chapterId, slug, buddy }) =>
  apiPost('/buddy-call/start', { chapter_id: chapterId, slug, buddy });

/**
 * POST /buddy-call/end { call_id } → { call_id, ended: true }. Idempotent. `keepalive` lets the
 * call made from `pagehide` outlive a closing tab. Contract §6.
 */
export const endBuddyCall = ({ callId }, { keepalive = false } = {}) =>
  apiPost('/buddy-call/end', { call_id: callId }, { keepalive });

/** GET /api/recall/questions?concept_id=…&set_id=… → { concept, setId, questions }.
 * Pure read of one pre-generated set — no side effects, creates no attempt row, so it is
 * safe to retry, prefetch and cache. Questions arrive in authored `position` order; the
 * server never re-orders them.
 * `concept.subject` is the concept's subject key, served but not rendered by Recall.
 * See src/pages/recall/RECALL_DATA.md. */
export const getRecallQuestions = ({ conceptId, setId }) =>
  apiGet(`/api/recall/questions?concept_id=${encodeURIComponent(conceptId)}&set_id=${encodeURIComponent(setId)}`);

/** POST /api/recall/start { concept_id, set_id } → { attemptId }. The ONLY attempt-creating
 * call — fired on the intro's Start Mission tap, never on mount, so a student who opens the
 * brief and backs out leaves no empty attempt row behind.
 * Callers queue the progress POSTs behind this promise, so it must stay genuinely async. */
export const startRecallAttempt = ({ conceptId, setId }) =>
  apiPost('/api/recall/start', { concept_id: conceptId, set_id: setId });

/** POST /api/recall/progress — one per Check, fire-and-forget. `attemptId` rides the wire as a
 * SIBLING of the answer fields, not as a nested object.
 * The backend must accept an answer that arrives AFTER `finish` for the same attempt: the
 * finish call does not wait on the in-flight progress POST. */
export const postRecallProgress = (attemptId, answer) =>
  apiPost('/api/recall/progress', { attemptId, ...answer });

/** POST /api/recall/hintused { attemptId } → { ok }. Fired at most ONCE per attempt, the first
 * time the student opens a REAL (authored) hint while `hintOnboarding` was set on /start.
 * Tells the backend the hint affordance has landed so it stops asking for that learner.
 * Fire-and-forget, and queued behind the start promise like the progress POSTs. */
export const reportRecallHintUsed = (attemptId) => apiPost('/api/recall/hintused', { attemptId });

/** POST /api/recall/finish { attemptId } → the results screen's whole summary:
 * { attemptId, conceptId, missionPass, learningObjectives, auraPoints, missionScore,
 *   totalQuestions, attempted, correctlyAnswered, nextMission }.
 * Finalizes the attempt. `missionScore` is a final integer percent (0..100) computed by the
 * server (`correctlyAnswered / totalQuestions`) — render it as-is, never recompute it.
 * `nextMission` carries the same `hasAccess` / `accessMessage` as a Home mission card. Only LOs with ≥1 answered question appear — no padding. Scores are
 * 0..1; the UI maps them to bar fills and tier labels.
 *
 * It carries NO leaderboard rank and NO quest progress: `auraPoints` is its only other
 * cross-feature field alongside `newAwards: NewAward[]` (default `[]`, camelCase like the rest
 * of this response). The post-mission wrap refetches /api/home/leaderboard/full and
 * /api/home/quests for rank/quests — see docs/wiki/post-mission-wrap.md. `newAwards` is
 * normalised and, if any arrived, used to call `prefetchAwardGrant` (see RecallRoute.jsx). */
export const finishRecallAttempt = (attemptId) => apiPost('/api/recall/finish', { attemptId });

/** INTENTIONALLY UNUSED as of 2026-09-18 — no caller since the Wyso redesign took the avatar
 *  out of Recall. Kept as the wire half of the Simli standalone announce unit
 *  (`shared/avatar/*`); delete it only together with that whole unit. See the header comment
 *  on `shared/avatar/simliSession.js` and the tech-debt entry. */
/** POST /api/avatar/announce — REAL from day one (backend may return demo text).
 * Single-use Simli compose token in the response — never cache or reuse. */
export const announceAvatar = (body) => apiPost('/api/avatar/announce', body);

// ---------------------------------------------------------------------------
// Signup — /auth/signup/* and /auth/invite/* (public, snake_case, no /api)
//
// All five are `{ auth: false }`: the visitor has no session, and a stale refresh token in
// storage must not trigger a token mint before a public call. They sit under `/auth/`, which
// `request()` also exempts from the 401-refresh-retry — correct here, since a 401 from any of
// them is an answer, not an expired token. Contract: src/pages/signup/SIGNUP_DATA.md.
// ---------------------------------------------------------------------------

/**
 * POST /auth/signup/suggest { name, grade } → { suggested_username, cities: [{code, label}] }.
 * Fires during the dive from step 1 → 2 and MUST NOT block it: on any failure the form falls
 * back to a derived username and `FALLBACK_CITIES`.
 */
export const suggestSignup = ({ name, grade }) =>
  apiPost('/auth/signup/suggest', { name, grade }, { auth: false });

/**
 * GET /auth/signup/username?u= → { available, suggestion? }.
 * Advisory only — called just when the student edits away from the served suggestion, and never
 * blocking. `POST /auth/signup`'s 409 is the authoritative answer.
 */
export const checkUsername = (username) =>
  apiGet(`/auth/signup/username?u=${encodeURIComponent(username)}`, { auth: false });

/**
 * POST /auth/signup → { username, status: 'locked' }. Creates the account in a LOCKED state:
 * it carries no tokens and cannot sign in until the invite code is redeemed. 409 = username
 * taken (bounce to step 2), 422 = validation.
 * `payload` is already snake_cased by `buildSignupPayload` in the page's own rules module.
 * Adds the device's first-touch `attribution` until one login or signup succeeds (see
 * lib/attribution.js).
 */
export const createAccount = async (payload) => {
  const attribution = pendingAttribution();
  const res = await apiPost('/auth/signup', attribution ? { ...payload, attribution } : payload, { auth: false });
  if (attribution) markAttributionSent();
  return res;
};

/**
 * POST /auth/invite/check { code } → { valid }.
 * ADVISORY. It never grants access — only `redeemInvite` does. It exists so a wrong code can
 * shake the card and tick the retry counter without spending a redeem attempt.
 */
export const checkInviteCode = (code) =>
  apiPost('/auth/invite/check', { code }, { auth: false });

/**
 * POST /auth/invite/redeem { username, code } → the SAME TokenResponse shape as /auth/login.
 * Unlocks the account and mints the session in one transaction, so the password is never sent
 * a second time. The caller stores the pair via `setSession`. 400/404 = bad or spent code.
 */
export const redeemInvite = ({ username, code }) =>
  apiPost('/auth/invite/redeem', { username, code }, { auth: false });

// Explore Away — no generated/demo fallback. Context lives on the server.
export const getExploreCategories = (options) => apiGet('/api/explore/categories', options);
export const getExploreConversation = (id, options) =>
  apiGet(`/api/explore/conversations/${encodeURIComponent(id)}`, options);
export const getExploreImage = (id, { wait = true, ...options } = {}) =>
  apiGet(`/api/explore/images/${encodeURIComponent(id)}${wait ? '?wait=true' : ''}`, options);
export const getExploreDiscoveries = (options) => apiGet('/api/explore/discoveries', options);
// `collection` is {kind:'existing', id} or {kind:'new', name}; the server creates or reuses it.
export const saveExploreDiscovery = ({ text, collection }) =>
  apiPost('/api/explore/discoveries', { text, collection });
// The server works out the Aura and time; ending twice is safe and the conversation stays open.
export const endExploreConversation = (id, options) =>
  apiPost(`/api/explore/conversations/${encodeURIComponent(id)}/end`, undefined, options);
export const deleteExploreDiscovery = (id) =>
  request('DELETE', `/api/explore/discoveries/${encodeURIComponent(id)}`);
export function sendExploreChat({ message, conversationId, categoryId, attachment }, options) {
  const body = new FormData();
  body.set('message', message);
  if (conversationId) body.set('conversation_id', conversationId);
  if (categoryId) body.set('categoryId', categoryId);
  if (attachment) body.set('attachment', attachment);
  return apiPost('/api/explore/chat', body, options);
}

/**
 * GET /api/events/:slug → { slug, name, startsAt, endsAt, venueName, venueAddress, venueMapsUrl, isOpen }.
 * Public. The wire is snake_case; camelCased here. 404 = no such event (don't retry).
 */
export const getEvent = async (slug) => {
  const e = await apiGet(`/api/events/${encodeURIComponent(slug)}`, { auth: false });
  return {
    slug: e.slug,
    name: e.name,
    startsAt: e.starts_at,
    endsAt: e.ends_at ?? null,
    venueName: e.venue_name,
    venueAddress: e.venue_address,
    venueMapsUrl: e.venue_maps_url ?? null,
    isOpen: e.is_open !== false,
  };
};

/**
 * POST /api/events/:slug/registrations → { ticketCode, passHref }. Public.
 * `payload` is already snake_cased by `buildRegistrationPayload` in the page's rules module.
 * 409 = registrations closed, 429 = too many attempts from this network, 422 = validation.
 * The wire's `pass_url` is relative to the API, so it is made absolute here: the PDF is served
 * by the backend, not by this app's origin.
 */
export const registerForEvent = async (slug, payload) => {
  const r = await apiPost(`/api/events/${encodeURIComponent(slug)}/registrations`, payload, { auth: false });
  return { ticketCode: r.ticket_code, passHref: new URL(r.pass_url, API_BASE_URL).href };
};
